import React, { useState, useEffect } from 'react';
import { applyAnydeskResetOnDeviceChange } from '../../utils/anydeskDeviceSwap';
import { deviceOptionLabel } from '../../utils/deviceOptionLabel';
import { devicesForItem, shouldClearDeviceOnTypeChange } from '../../utils/rentalDeviceOptions';
import { excludePendingReturn } from '../../utils/devicePickerFilter';
import { excludeSimsPendingReturn } from '../../utils/simAvailability';
import { simOptionLabel } from '../../utils/simOptionLabel';
import { rentalHoldFor, withRentalHoldLabel } from '../../utils/rentalHold';
import type { RentalHoldInfo } from '../../types/rentalHold';
import { useNavigate, useParams } from 'react-router-dom';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import Layout from '../../components/ui/Layout';
import SearchableSelect from '../../components/ui/SearchableSelect';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { RiAddLine, RiDeleteBinLine, RiSaveLine, RiArrowLeftLine, RiFileTextLine, RiForbid2Line, RiAlertLine, RiInformationLine, RiCloseLine, RiEditLine, RiWalletLine, RiListCheck, RiCalculatorLine, RiGiftLine, RiCalendarLine, RiRemoteControlLine } from 'react-icons/ri';
import RentalNotesModal from '../../components/rental/RentalNotesModal';
import companyService, { CompanyDepositSettings, CompanyTotalDepositInfo } from '../../services/companyService';
import siteService from '../../services/siteService';
import productService from '../../services/productService';
import deviceService from '../../services/deviceService';
import deviceTypeService from '../../services/deviceTypeService';
import simService from '../../services/simService';
import rentalService from '../../services/rentalService';
import PrepaidUntilFields from '../../components/rental/PrepaidUntilFields';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePermission } from '../../hooks/usePermission';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { cn } from '../../utils/cn';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { countSerializedDevices, MULTI_SERIALIZED_WARNING } from '../../utils/serializedDeviceCount';

// ประเภทข้อมูลสำหรับฟอร์ม
interface RentalFormData {
  rental_number: string;
  site_id: number | '';
  company_id: number | '';
  rental_date: string;
  billing_start_date: string; // วันที่เริ่มเก็บเงิน
  billing_end_date: string;   // วันสิ้นสุดเก็บเงิน (nullable — ส่ง null เมื่อ empty)
  prepaid_until: string;      // ชำระล่วงหน้าถึงวันที่ (ว่าง = ไม่ได้ชำระล่วงหน้า → ส่ง null)
  prepaid_note: string;
  expected_return_date: string;
  rental_period_days: number;
  notes: string;
  rental_items: RentalItemFormData[];
  updated_by?: number | null;
  // Auto-renewal fields
  auto_renew: boolean;
  renewal_notice_days: number;
  renewal_terms: string;
  end_date: string;
  // New fields for data migration
  delivery_date: string; // วันที่ส่งมอบอุปกรณ์
}

interface RentalItemFormData {
  id?: string | number; // สำหรับ tracking ใน frontend
  product_id: number | ''; // GAP-29: เพิ่ม product_id สำหรับเลือกสินค้า
  device_type_id: number | '';
  device_id: number | '';
  sim_id: number | ''; // เพิ่ม sim_id
  quantity: number;
  rental_rate_per_month: number; // ค่าเช่าต่อเดือน
  deposit_amount: number;
  deposit_waived: boolean; // ยกเว้นค่ามัดจำ
  notes: string;
  // New fields for data migration - deposit tracking
  deposit_status: 'PENDING' | 'PAID' | 'RETURNED' | 'WAIVED' | 'PENDING_REFUND'; // สถานะมัดจำ
  deposit_paid_date: string; // วันที่โอนค่ามัดจำ
  deposit_returned_date: string; // วันที่คืนค่ามัดจำ
  // Deposit v2: คงมัดจำไว้ก่อน (มีผลเฉพาะ PENDING_REFUND) + หมายเหตุเหตุผล
  deposit_hold: boolean;
  deposit_hold_note: string;
  // สถานะมัดจำที่บันทึกอยู่ (ค่าจาก server) — PENDING_REFUND/RETURNED ล็อค select ให้ไปจัดการในหน้ารายละเอียด
  _original_deposit_status?: string;
  is_software_only: boolean; // เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
  // Remote Access (AnyDesk) — ต่อเครื่อง (REPLACE STRATEGY: ต้อง init + ส่งกลับเสมอ)
  anydesk_id: string;
  remote_access_notes: string;
  // Frontend-only tracking fields for change detection
  _prev_device_type_id?: number | '';
  _prev_device_id?: number | '';
  // UI-only: ล้าง AnyDesk ให้แล้วเพราะเปลี่ยนเครื่อง (โชว์ข้อความใต้ช่อง · ไม่ส่งไป backend)
  _anydesk_cleared?: boolean;
  _prev_deposit_amount?: number;
}

// ประเภทข้อมูลสำหรับ dropdown options
interface DropdownOption {
  id: number;
  name: string;
  code?: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
}

interface Site {
  id: number;
  name: string;
  code: string;
  company_id: number;
}

interface Product {
  id: number;
  name: string;
  code: string;
  rental_rate?: number;
}

interface Device {
  id: number;
  serial_number: string;
  code?: string;
  device_type_id?: number;
  device_type?: {
    id: number;
    name: string;
    code: string;
  };
  device_model_id?: number;
  device_model?: { id: number; name: string };
  status: string;
  model?: string;
  // Inventory fields
  is_serialized?: boolean;
  quantity?: number;
  unit?: string;
  // fix ค (20 ก.ย. 2026): "พร้อมใช้งานแต่ยังถูกล็อก" ด้วยสัญญาอื่นที่ยังไม่จบ — ต้องเลือกได้อยู่
  // แต่ติดป้ายเตือนก่อนเลือก (ดู utils/rentalHold.ts)
  rental_hold?: RentalHoldInfo | null;
}

interface Sim {
  id: number;
  phone_number: string;
  serial_number?: string;
  operator: string;
  sim_type: string;
  status: string;
  // fix ค (20 ก.ย. 2026) — ดูคอมเมนต์เดียวกันที่ interface Device
  rental_hold?: RentalHoldInfo | null;
}

const RentalEditPageV2: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // BUG-S2283-001: id ต้องเป็นจำนวนเต็มบวกล้วน ("487abc"/"1e3" เดิมเปิด/บันทึกทับรายการเช่าผิดตัว)
  const recordId = parseRouteId(id);
  const { getUserId, hasValidUser } = useCurrentUser();
  const { canEdit, hasPermission } = usePermission();
  // Deposit v2: เปลี่ยนสถานะมัดจำเป็น RETURNED/PENDING_REFUND ผ่าน PUT ต้องมีสิทธิ์นี้ (backend 403 ถ้าไม่มี)
  const canManageDeposit = hasPermission('rentals.deposit_manage');
  // E2E v6 OBS-1 (PO 2026-09-03): ยอดมัดจำของแถวที่เงินเข้าระบบแล้ว (ชำระแล้ว/รอคืน/คืนแล้ว) เป็นงานบัญชี
  // — backend 403 ถ้าไม่มี rentals.deposit_manage → ล็อคช่องให้ตรงกัน (แถวรอชำระ/ยกเว้น แก้ได้ตามเดิม)
  const DEPOSIT_AMOUNT_LOCK_HINT = 'ยอดมัดจำที่ชำระแล้ว/รอคืน/คืนแล้ว แก้ได้เฉพาะผู้มีสิทธิ์บันทึกรับ/คืน/คงเงินมัดจำ';
  const depositAmountLocked = (it: { _original_deposit_status?: string }) =>
    !canManageDeposit && ['PAID', 'PENDING_REFUND', 'RETURNED'].includes(it._original_deposit_status || '');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  // BUG-178 FIX: per-field loading indicators for device dropdowns
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [loadingSims, setLoadingSims] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Unsaved changes warning
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isFormDirty);

  // NaN guard: ตรวจสอบ id ว่าเป็นตัวเลขที่ถูกต้อง
  useEffect(() => {
    if (recordId === null) {
      navigate('/rentals', { replace: true });
    }
  }, [recordId, navigate]);

  // Permission check
  useEffect(() => {
    if (!canEdit('rentals')) {
      navigate('/rentals/list');
    }
  }, [canEdit, navigate]);

  // ข้อมูลฟอร์ม
  const [formData, setFormData] = useState<RentalFormData>({
    rental_number: '',
    site_id: '',
    company_id: '',
    rental_date: '',
    billing_start_date: '', // วันที่เริ่มเก็บเงิน
    billing_end_date: '',   // วันสิ้นสุดเก็บเงิน
    prepaid_until: '',      // ชำระล่วงหน้าถึงวันที่
    prepaid_note: '',
    expected_return_date: '',
    rental_period_days: 0,
    notes: '',
    rental_items: [],
    updated_by: null,
    // Auto-renewal fields
    auto_renew: false,
    renewal_notice_days: 30,
    renewal_terms: '',
    end_date: '',
    // New fields for data migration
    delivery_date: ''
  });

  // ข้อมูลต้นฉบับสำหรับเปรียบเทียบ
  const [originalRentalData, setOriginalRentalData] = useState<any>(null);

  // device movements Phase 1: rental ที่ไม่ใช่ DRAFT ห้ามเปลี่ยนโครงการจากหน้าแก้ไข
  // (backend PUT /rentals/{id} ปฏิเสธ site_id ใหม่ด้วย 400) — ต้องใช้เมนู "ย้ายอุปกรณ์" แทน
  const siteLocked = !!originalRentalData && originalRentalData.status !== 'DRAFT';

  // ข้อมูลสำหรับ dropdown
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [sims, setSims] = useState<Sim[]>([]);
  const [availableSims, setAvailableSims] = useState<Sim[]>([]);

  // ข้อมูลค่ามัดจำรวมของบริษัท
  const [companyDepositInfo, setCompanyDepositInfo] = useState<CompanyTotalDepositInfo | null>(null);

  // การตั้งค่าค่ามัดจำของบริษัท
  const depositSettings = React.useMemo<CompanyDepositSettings>(() => {
    if (companyDepositInfo) {
      return {
        deposit_amount: companyDepositInfo.deposit_limit
      };
    }
    return {
      deposit_amount: 40000
    };
  }, [companyDepositInfo]);

  // อัพเดท updated_by เมื่อ user โหลดเสร็จ
  useEffect(() => {
    if (hasValidUser) {
      const userId = getUserId();
      setFormData(prev => ({
        ...prev,
        updated_by: userId
      }));
    }
  }, [hasValidUser, getUserId]);

  // โหลดข้อมูลเริ่มต้น
  useEffect(() => {
    // id ไม่ถูกต้อง → ไม่ยิง API (effect ด้านบน redirect ไปหน้ารายการ)
    if (recordId !== null) {
      loadInitialData();
      loadRentalData();
    }
  }, [recordId]);

  // กรองไซต์ตามบริษัทที่เลือก และโหลดข้อมูล
  useEffect(() => {
    if (formData.company_id) {
      const filtered = sites.filter(site => site.company_id === formData.company_id);
      setFilteredSites(filtered);
      
      // โหลดข้อมูลค่ามัดจำรวม
      loadCompanyTotalDeposit(formData.company_id);
    } else {
      setFilteredSites([]);
      setCompanyDepositInfo(null);
    }
  }, [formData.company_id, sites]);

  // คำนวณระยะเวลาเมื่อมีการเปลี่ยนแปลงวันที่
  useEffect(() => {
    if (formData.rental_date && formData.expected_return_date) {
      const startDate = new Date(formData.rental_date);
      const endDate = new Date(formData.expected_return_date);
      
      if (startDate <= endDate) {
        const diffTime = endDate.getTime() - startDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        
        setFormData(prev => ({
          ...prev,
          rental_period_days: diffDays,
          rental_items: prev.rental_items.map(item => ({
            ...item,
            rental_period_days: diffDays
          }))
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          rental_period_days: 1,
          rental_items: prev.rental_items.map(item => ({
            ...item,
            rental_period_days: 1
          }))
        }));
      }
    }
  }, [formData.rental_date, formData.expected_return_date]);

  const loadInitialData = async () => {
    try {
      // โหลดข้อมูลบริษัท
      try {
        const companiesResponse = await companyService.getCompanies({ page: 1, limit: 1000 });
        setCompanies((companiesResponse.items || []) as any);
      } catch (error) {
        console.error('Error loading companies:', error);
        setCompanies([]);
      }

      // โหลดข้อมูลไซต์ที่ active เท่านั้น
      try {
        const sitesResponse = await siteService.getSites({ page: 1, limit: 1000, status: 'active' });
        setSites(sitesResponse.items || []);
      } catch (error) {
        console.error('Error loading sites:', error);
        setSites([]);
      }

      // โหลดข้อมูลสินค้าที่ active เท่านั้น
      try {
        const productsResponse = await productService.getProducts({ page: 1, limit: 100, is_active: true });
        setProducts(productsResponse.items || []);
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts([]);
      }

      // โหลดข้อมูลประเภทอุปกรณ์
      // BUG-178 FIX: per-field loading indicator
      setLoadingDeviceTypes(true);
      try {
        const deviceTypesResponse = await deviceTypeService.getDeviceTypes({ page: 1, limit: 100 });
        if (deviceTypesResponse && deviceTypesResponse.items && Array.isArray(deviceTypesResponse.items)) {
          setDeviceTypes(deviceTypesResponse.items);
        } else {
          setDeviceTypes([]);
        }
      } catch (error) {
        console.error('Error loading device types:', error);
        setDeviceTypes([]);
      } finally {
        setLoadingDeviceTypes(false);
      }

      // โหลดข้อมูลอุปกรณ์ทั้งหมด
      // BUG-178 FIX: per-field loading indicator
      setLoadingDevices(true);
      try {
        // BUG-S592-AVAILCAP: กรองอุปกรณ์ว่างฝั่ง client แต่โหลดแค่ 100 จาก 689 ตัว
        const devicesResponse = await deviceService.getDevices({
          page: 1,
          limit: 1000
        });

        if (devicesResponse && devicesResponse.items && Array.isArray(devicesResponse.items)) {
          const allDevices = devicesResponse.items as any;
          setDevices(allDevices);
        } else {
          setDevices([]);
        }
      } catch (error) {
        console.error('Error loading devices:', error);
        setDevices([]);
      } finally {
        setLoadingDevices(false);
      }

      // โหลดข้อมูลซิมการ์ด (เฉพาะเมื่อไม่ได้อยู่ในโหมดแก้ไข)
      // ใน edit mode จะโหลด SIM ใน loadRentalData() แทน
      if (!id) {
        // BUG-178 FIX: per-field loading indicator
        setLoadingSims(true);
        try {
          const simsResponse = await simService.getAllSims();

          if (simsResponse && simsResponse.items && Array.isArray(simsResponse.items)) {
            const allSims = simsResponse.items;
            // Lane D (L2 ฝั่งซิม): สาขานี้คือโหมด "สร้างใหม่" (ไม่มี id) → ไม่มีซิมเดิมให้คงไว้
            const availableSims = excludeSimsPendingReturn(
              allSims.filter(sim => {
                const s = String(sim.status);
                return s === 'AVAILABLE' || s === 'available' || s === 'พร้อมใช้งาน';
              })
            );
            setSims(allSims);
            setAvailableSims(availableSims);
          } else {
            setSims([]);
            setAvailableSims([]);
          }
        } catch (error) {
          console.error('Error loading sims:', error);
          setSims([]);
          setAvailableSims([]);
        } finally {
          setLoadingSims(false);
        }
      }

    } catch (error) {
      console.error('Error loading initial data:', error);
      showAlert('เกิดข้อผิดพลาด', 'เกิดข้อผิดพลาดในการโหลดข้อมูล', 'error');
    }
  };

  const loadRentalData = async () => {
    if (recordId === null) return;
    try {
      setPageLoading(true);
      // ใช้ API endpoint สำหรับการแก้ไขที่มี rental_items ครบถ้วน
      const rental = await rentalService.getRentalForEdit(recordId);
      
      setOriginalRentalData(rental);
      
      // แปลงข้อมูลให้เหมาะกับฟอร์ม
      const formattedItems = await Promise.all(
        (rental.rental_items || []).map(async (item: any) => {
          let deviceTypeId = '';
          
          // ใช้ device_type_id จาก device object ถ้ามี หรือโหลดเพิ่มเติม
          if (item.device && item.device.device_type_id) {
            deviceTypeId = item.device.device_type_id;
          } else if (item.device_id) {
            try {
              const deviceData = await deviceService.getDevice(item.device_id);
              deviceTypeId = deviceData.device_type_id ? String(deviceData.device_type_id) : '';
            } catch (error) {
              console.error(`Error loading device ${item.device_id}:`, error);
            }
          }
          
          
          const formattedItem = {
            id: item.id,
            product_id: item.product_id || (item.product ? item.product.id : ''), // GAP-29: เพิ่ม product_id
            device_type_id: item.is_software_only ? '' : deviceTypeId,
            device_id: item.is_software_only ? '' : (item.device_id || ''),
            sim_id: item.sim_id || '', // เพิ่ม sim_id
            quantity: item.quantity || 1,
            rental_rate_per_month: item.rental_rate || 0,
            deposit_amount: item.deposit_amount || 0, // ใช้ค่ามัดจำจาก item โดยตรง
            deposit_waived: item.deposit_waived || false,
            notes: item.notes || '',
            // New fields for data migration - deposit tracking
            deposit_status: item.deposit_status || 'PENDING',
            deposit_paid_date: item.deposit_paid_date ? item.deposit_paid_date.split('T')[0] : '',
            deposit_returned_date: item.deposit_returned_date ? item.deposit_returned_date.split('T')[0] : '',
            // Deposit v2
            deposit_hold: item.deposit_hold || false,
            deposit_hold_note: item.deposit_hold_note || '',
            _original_deposit_status: item.deposit_status || 'PENDING',
            is_software_only: item.is_software_only || false, // เช่า Software อย่างเดียว
            // Remote Access (AnyDesk) — preserve จาก response (REPLACE STRATEGY)
            anydesk_id: item.anydesk_id || '',
            remote_access_notes: item.remote_access_notes || ''
          };
          
          return formattedItem;
        })
      );
      
      const finalFormData = {
        rental_number: rental.rental_number,
        site_id: rental.site_id,
        company_id: rental.company_id,
        rental_date: rental.rental_date ? rental.rental_date.split('T')[0] : '',
        // BUG-UI-03: เดิม fallback เป็น `rental_date` เมื่อค่าจริงเป็น NULL → เปิดฟอร์มแล้วกดบันทึก
        // เฉย ๆ ก็เขียนวันเริ่มเก็บเงินที่ผู้ใช้ไม่เคยกรอกลงไป (ช่องนี้ "ไม่บังคับ" ดู BUG-S335-001)
        // เห็นชัดกับสัญญาที่มาจากใบงานเตรียมเครื่องซึ่งเริ่มด้วย NULL ทุกใบ
        // — bug class form-field-overwrite: ห้ามเดาค่าแทนผู้ใช้ ปล่อยว่างไว้ตามความจริง
        billing_start_date: rental.billing_start_date
          ? rental.billing_start_date.split('T')[0]
          : '',
        billing_end_date: rental.billing_end_date ? rental.billing_end_date.split('T')[0] : '',
        prepaid_until: rental.prepaid_until ? rental.prepaid_until.split('T')[0] : '',
        prepaid_note: rental.prepaid_note || '',
        expected_return_date: rental.expected_return_date ? rental.expected_return_date.split('T')[0] : '',
        rental_period_days: 0, // จะคำนวณใหม่จาก useEffect
        notes: rental.notes || '',
        rental_items: formattedItems,
        // Auto-renewal fields
        auto_renew: rental.auto_renew || false,
        renewal_notice_days: rental.renewal_notice_days || 30,
        renewal_terms: rental.renewal_terms || '',
        end_date: rental.end_date ? rental.end_date.split('T')[0] : '',
        // New fields for data migration
        delivery_date: rental.delivery_date ? rental.delivery_date.split('T')[0] : ''
      };
      
      setFormData(finalFormData);

      // โหลดอุปกรณ์และซิมที่พร้อมใช้งาน รวมกับที่กำลังใช้ในการเช่านี้
      if (rental.rental_items) {
        const rentalDeviceIds = rental.rental_items.map((item: any) => item.device_id).filter(Boolean);
        const rentalSimIds = rental.rental_items.map((item: any) => item.sim_id).filter(Boolean);
        
        // ดึงอุปกรณ์ทั้งหมดใหม่เพื่อให้แน่ใจว่าได้ข้อมูลล่าสุด
        try {
          // BUG-S592-AVAILCAP: โหลดแค่ 100 จาก 689 → อุปกรณ์ว่าง + อุปกรณ์ของการเช่านี้อาจไม่ติดมา
          const devicesResponse = await deviceService.getDevices({ 
            page: 1, 
            limit: 1000
          });
          
          if (devicesResponse && devicesResponse.items && Array.isArray(devicesResponse.items)) {
            const allDevices = devicesResponse.items;
            
            // กรองอุปกรณ์ที่พร้อมใช้งาน + อุปกรณ์ที่ใช้ในการเช่านี้
            // R4U-2: ตัดเครื่องที่ยังมีใบตามเก็บค้างออกตั้งแต่ตรงนี้ (ตัวนับ "อุปกรณ์พร้อมใช้: N" จะได้ตรงกับ
            // ตัวเลือกจริง) — ตัวเลือกต่อแถวใน devicesForItem กรองซ้ำอีกชั้นด้วยกติกาเดียวกัน · เครื่องของสัญญานี้
            // (rentalDeviceIds) คงไว้เสมอ ไม่งั้นแถวเดิมกลับเป็น "-- เลือกอุปกรณ์ --"
            const availableDevices = excludePendingReturn(
              allDevices.filter(device => {
                const s = String(device.status);
                return s === 'AVAILABLE' || s === 'available' || s === 'พร้อมใช้งาน' || rentalDeviceIds.includes(device.id);
              }),
              rentalDeviceIds,
            );
            
            setAvailableDevices(availableDevices as any);
            setDevices(allDevices as any); // อัพเดท devices list ด้วย
          }
        } catch (error) {
          console.error('Error loading devices for edit:', error);
        }

        // ดึงซิมทั้งหมดใหม่และกรอง
        try {
          const simsResponse = await simService.getAllSims();
          
          if (simsResponse && simsResponse.items && Array.isArray(simsResponse.items)) {
            const allSims = simsResponse.items;
            
            // กรองซิมที่พร้อมใช้งาน + ซิมที่ใช้ในการเช่านี้
            // Lane D (L2 ฝั่งซิม): ตัดซิมที่ยังมีใบตามเก็บค้างออก — **แต่คงซิมของสัญญานี้ไว้เสมอ**
            // (rentalSimIds) ไม่งั้นแถวเดิมกลับเป็น "-- เลือก SIM --" ทั้งที่สัญญาถือซิมนั้นอยู่จริง
            // · ด่าน backend ก็ดูเฉพาะใบตามเก็บของ "สัญญาอื่น" เหมือนกัน (parity กับฝั่งเครื่อง)
            const availableSims = excludeSimsPendingReturn(
              allSims.filter(sim => {
                const s = String(sim.status);
                const isAvailable = s === 'AVAILABLE' || s === 'available' || s === 'พร้อมใช้งาน';
                const isUsedInThisRental = rentalSimIds.includes(sim.id);


                return isAvailable || isUsedInThisRental;
              }),
              rentalSimIds,
            );

            setAvailableSims(availableSims);
            setSims(allSims); // อัพเดท sims list ด้วย
          }
        } catch (error) {
          console.error('Error loading sims for edit:', error);
        }
      }

    } catch (error: any) {
      console.error('Error loading rental data:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลการเช่าได้', 'error');
      navigate('/rentals/list');
    } finally {
      setPageLoading(false);
    }
  };

  // โหลดข้อมูลค่ามัดจำรวมของบริษัท
  const loadCompanyTotalDeposit = async (companyId: number) => {
    try {
      const data = await companyService.getTotalDeposit(companyId);
      setCompanyDepositInfo(data);
    } catch (error) {
      console.error('Error loading company total deposit:', error);
      setCompanyDepositInfo(null);
    }
  };

  // จัดการการเปลี่ยนแปลงข้อมูลรายการ
  const updateRentalItem = (index: number, field: keyof RentalItemFormData, value: any) => {
    const updatedItems = [...formData.rental_items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // เปลี่ยนประเภทอุปกรณ์ของ "รายการนี้" — ล้างเครื่องที่เลือกไว้ถ้าไม่ตรงประเภทใหม่
    // BUG (feedback amintra 3 ก.ย. 2026, /rentals/edit/90): เดิมเขียนทับ availableDevices (state ร่วมทุกรายการ)
    // ให้เหลือแต่ประเภทใหม่ → รายการอื่นที่ประเภทต่างกันไม่เหลือตัวเลือก ช่องกลับเป็น "-- เลือกอุปกรณ์ --"
    // และค้นหา SN ไม่เจอ · ตอนนี้ตัวเลือกกรองต่อรายการจาก availableDevices ตรง ๆ (ดู devicesForItem ตอน render)
    if (field === 'device_type_id' && value) {
      const currentRentalDeviceIds = formData.rental_items.map(item => item.device_id);
      if (shouldClearDeviceOnTypeChange(devices, value, updatedItems[index].device_id, currentRentalDeviceIds)) {
        updatedItems[index].device_id = '';
      }
    }

    // AnyDesk เป็นของ "เครื่อง" — เปลี่ยน/ถอดเครื่องของแถวนี้แล้ว ค่าเดิมไม่ใช่ของเครื่องใหม่ → ล้างช่อง
    // (mirror backend: PUT /rentals ที่สลับเครื่องในแถวเดิมล้าง anydesk_id ให้เหมือนกัน · 23 ก.ย. 2026)
    updatedItems[index] = applyAnydeskResetOnDeviceChange(formData.rental_items[index], updatedItems[index], field);

    setFormData(prev => ({ ...prev, rental_items: updatedItems }));
  };

  // เพิ่มรายการใหม่
  const addRentalItem = () => {
    const newItem: RentalItemFormData = {
      id: `new-${Date.now()}`,
      product_id: '', // GAP-29: เพิ่ม product_id
      device_type_id: '',
      device_id: '',
      sim_id: '', // เพิ่ม sim_id
      quantity: 1,
      rental_rate_per_month: 0,
      deposit_amount: 0,
      deposit_waived: false,
      notes: '',
      // New fields for data migration - deposit tracking
      deposit_status: 'PENDING',
      deposit_paid_date: '',
      deposit_returned_date: '',
      deposit_hold: false,
      deposit_hold_note: '',
      is_software_only: false, // เช่า Software อย่างเดียว
      // Remote Access (AnyDesk)
      anydesk_id: '',
      remote_access_notes: ''
    };

    setFormData(prev => ({
      ...prev,
      rental_items: [...prev.rental_items, newItem]
    }));
  };

  // ลบรายการ
  const removeRentalItem = (index: number) => {
    if (formData.rental_items.length > 1) {
      const updatedItems = formData.rental_items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, rental_items: updatedItems }));
    }
  };

  // คำนวณยอดรวม
  const calculateTotals = () => {
    // คำนวณมูลค่าการเช่ารวมตามจำนวนวันจริง
    const totalAmount = formData.rental_items.reduce((sum, item) => {
      const dailyRate = (item.rental_rate_per_month || 0) / 30; // คำนวณค่าเช่าต่อวัน (หาร 30)
      const totalDays = formData.rental_period_days || 0; // ใช้จำนวนวันจากฟอร์มหลัก
      return sum + (dailyRate * (item.quantity || 1) * totalDays);
    }, 0);
    
    const totalDeposit = formData.rental_items.reduce((sum, item) => {
      // นับเฉพาะค่ามัดจำที่ไม่ได้ถูกยกเว้น
      return sum + (item.deposit_waived ? 0 : (item.deposit_amount || 0));
    }, 0);
    
    // คำนวณมูลค่าการเช่าต่อเดือน
    const monthlyRentalValue = formData.rental_items.reduce((sum, item) => {
      return sum + ((item.rental_rate_per_month || 0) * (item.quantity || 1));
    }, 0);
    
    // คำนวณมูลค่าการเช่าต่อปี (12 เดือน)
    const yearlyRentalValue = monthlyRentalValue * 12;
    
    return { totalAmount, totalDeposit, monthlyRentalValue, yearlyRentalValue };
  };

  // ตรวจสอบข้อมูลฟอร์ม
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.rental_number.trim()) {
      errors.rental_number = 'กรุณากรอกเลขที่การเช่า';
    }

    if (!formData.company_id) {
      errors.company_id = 'กรุณาเลือกบริษัท';
    }

    if (!formData.site_id) {
      errors.site_id = 'กรุณาเลือกโครงการ';
    }

    if (!formData.rental_date) {
      errors.rental_date = 'กรุณาเลือกวันที่เช่า';
    }

    // edit: วันที่คาดว่าจะครบสัญญาไม่บังคับ (record เก่า NULL ต้อง save ได้)
    // BUG-S335-001 FIX: expected_return_date < rental_date guard (parity กับ Create L583-584 +
    // backend S327). เดิม Edit ขาด → return-ก่อน-เช่า หลุด inline UX (backend กันแต่ user เห็นช้า).
    if (formData.expected_return_date && formData.rental_date && formData.expected_return_date < formData.rental_date) {
      errors.expected_return_date = 'วันที่คืนต้องไม่น้อยกว่าวันที่เช่า';
    }

    // BUG-S335-001 FIX: billing_start_date "ไม่บังคับ" (parity กับ Create L587-589 +
    // PO directive 2026-06-24 "ห้าม revert กลับเป็น required"). เดิม Edit บังคับ →
    // rental ที่มี NULL billing_start (สร้างแบบ optional) แก้ field อื่นไม่ได้จนกว่าเพิ่ม
    // billing_start → เปลี่ยนพฤติกรรม proration โดยไม่ตั้งใจ. ปล่อยว่าง = backend fallback ใช้วันที่เช่า.
    if (formData.billing_start_date && formData.rental_date && formData.billing_start_date < formData.rental_date) {
      errors.billing_start_date = 'วันที่เริ่มเก็บเงินต้องไม่น้อยกว่าวันที่เช่า';
    }
    // ชำระล่วงหน้าถึงวันที่ ต้องไม่ก่อนวันเริ่มเก็บเงิน (parity กับ backend)
    {
      const _prepaidStart = formData.billing_start_date || formData.rental_date;
      if (formData.prepaid_until && _prepaidStart && formData.prepaid_until < _prepaidStart) {
        errors.prepaid_until = 'วันที่ชำระล่วงหน้าถึงต้องไม่น้อยกว่าวันที่เริ่มเก็บเงิน';
      }
    }
    // ตรวจสอบว่า billing_start_date ต้องไม่เกินวันที่คาดว่าจะครบสัญญา
    if (formData.billing_start_date && formData.expected_return_date && formData.billing_start_date > formData.expected_return_date) {
      errors.billing_start_date = 'วันที่เริ่มเก็บเงินต้องไม่เกินวันที่คาดว่าจะครบสัญญา';
    }

    // BUG-S2171-001: form เป็น noValidate → min/max ของช่องไม่เคยถูกบังคับ (91 บันทึกผ่าน)
    if (formData.auto_renew) {
      const d = Number(formData.renewal_notice_days);
      if (!Number.isInteger(d) || d < 1 || d > 90) {
        errors.renewal_notice_days = 'จำนวนวันแจ้งเตือนล่วงหน้าต้องเป็นจำนวนเต็ม 1–90 วัน';
      }
    }

    // ตรวจสอบรายการอุปกรณ์
    formData.rental_items.forEach((item, index) => {
      if (!item.is_software_only) {
        if (!item.device_type_id) {
          errors[`item_${index}_device_type`] = `กรุณาเลือกประเภทอุปกรณ์สำหรับรายการที่ ${index + 1}`;
        }
        if (!item.device_id) {
          errors[`item_${index}_device`] = `กรุณาเลือกอุปกรณ์สำหรับรายการที่ ${index + 1}`;
        }
      }
      if (item.rental_rate_per_month === null || item.rental_rate_per_month === undefined || item.rental_rate_per_month < 0) {
        errors[`item_${index}_rate`] = `ค่าเช่าต่อเดือนต้องไม่ติดลบสำหรับรายการที่ ${index + 1}`;
      }
      // BUG-S336-001 FIX (parity กับ Create L611-613): ค่ามัดจำต้องไม่ติดลบ
      if (!item.deposit_waived && item.deposit_amount < 0) {
        errors[`item_${index}_deposit`] = `ค่ามัดจำต้องไม่ติดลบสำหรับรายการที่ ${index + 1}`;
      }
      // BUG-S336-001 FIX (parity กับ Create L615-643): กันเลือกอุปกรณ์ซ้ำภายในสัญญาเดียว
      // (intra-rental device dup — Create กันแต่ Edit เดิมไม่กัน → OBS-S1374 rental 679 มี 4 items
      // device เดียวกัน = double-count device/billing/deposit ในสัญญา). serialized=ห้ามซ้ำ+qty 1 ·
      // counted=ตรวจผลรวมไม่เกิน stock.
      if (item.device_id) {
        const selectedDevice = devices.find(d => d.id === item.device_id);
        if (selectedDevice) {
          if (selectedDevice.is_serialized) {
            const duplicateItems = formData.rental_items.filter((otherItem, otherIndex) =>
              otherIndex !== index && otherItem.device_id === item.device_id
            );
            if (duplicateItems.length > 0) {
              errors[`item_${index}_device`] = `อุปกรณ์นี้ถูกเลือกแล้วในรายการอื่น ไม่สามารถเลือกซ้ำได้`;
            }
            if (item.quantity > 1) {
              errors[`item_${index}_quantity`] = `อุปกรณ์ที่มี Serial Number เลือกได้เพียง 1 ชิ้น`;
            }
          } else {
            const totalUsedInOtherItems = formData.rental_items
              .filter((otherItem, otherIndex) => otherIndex !== index && otherItem.device_id === item.device_id)
              .reduce((sum, otherItem) => sum + (otherItem.quantity || 0), 0);
            const totalRequestedQuantity = totalUsedInOtherItems + (item.quantity || 0);
            if (totalRequestedQuantity > selectedDevice.quantity) {
              errors[`item_${index}_quantity`] = `จำนวนรวมที่เลือก (${totalRequestedQuantity}) เกินจำนวนที่มีอยู่ (${selectedDevice.quantity})`;
            }
          }
        }
      }
    });

    // วงเงินมัดจำต่อบริษัท = advisory เท่านั้น (PO) — "ไม่บล็อก" การบันทึกเมื่อเกินวงเงิน
    // ผู้สร้างเห็นเตือนสีแดงใน Panel/สรุปยอด + ตัดสินใจเอง (ติ๊กฟรีมัดจำรายเครื่องได้)
    // จึงไม่ใส่ deposit_limit ลงใน errors อีกต่อไป (เดิม block save → ขัด contract)

    // ถ้ามี errors ให้ set inline errors และ scroll to first error
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"], [id="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return false;
    }

    setFormErrors({});
    return true;
  };

  // บันทึกข้อมูล
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // total_amount = run-rate ต่อเดือน (มูลค่าจริงของ subscription) — เงินจริงคิดจาก proration ตอนออก invoice
      const { totalDeposit, monthlyRentalValue } = calculateTotals();

      const userId = getUserId();
      // GAP-2: ใช้ userId ตรง ไม่ fallback เป็น hardcoded ID
      const finalUserId = userId;
      
      const rentalData = {
        rental_number: formData.rental_number,
        site_id: formData.site_id,
        company_id: formData.company_id,
        rental_date: new Date(formData.rental_date),
        billing_start_date: formData.billing_start_date ? new Date(formData.billing_start_date) : null,
        billing_end_date: formData.billing_end_date ? new Date(formData.billing_end_date) : null,
        // ชำระล่วงหน้า: ฟอร์มโหลดค่าเดิมมาใส่แล้ว → ว่าง = ผู้ใช้ล้างเอง จึงส่ง null เพื่อล้างจริง
        prepaid_until: formData.prepaid_until || null,
        prepaid_note: formData.prepaid_note.trim() || null,
        expected_return_date: formData.expected_return_date ? new Date(formData.expected_return_date) : null,
        total_amount: monthlyRentalValue,
        deposit_amount: totalDeposit,
        notes: formData.notes,
        updated_by: finalUserId,
        // Auto-renewal fields
        auto_renew: formData.auto_renew,
        renewal_notice_days: formData.renewal_notice_days,
        renewal_terms: formData.renewal_terms || null,
        end_date: formData.end_date ? new Date(formData.end_date) : null,
        // New fields for data migration
        delivery_date: formData.delivery_date ? new Date(formData.delivery_date) : null,
        rental_items: formData.rental_items.map(item => ({
          id: typeof item.id === 'string' && item.id.startsWith('new-') ? undefined : item.id,
          product_id: item.product_id || null, // GAP-29: ส่ง product_id
          device_type_id: item.is_software_only ? null : item.device_type_id,
          device_id: item.is_software_only ? null : item.device_id,
          sim_id: item.sim_id || null, // เพิ่ม sim_id
          quantity: item.quantity,
          rental_rate_per_month: item.rental_rate_per_month,
          // total_amount = ค่าเช่าต่อเดือน (run-rate) — ไม่คูณระยะเวลา; เงินจริงคิดจาก proration ตอนออก invoice
          total_amount: (item.rental_rate_per_month || 0) * (item.quantity || 1),
          // วงเงินมัดจำต่อบริษัท (advisory): ติ๊กฟรี → deposit_amount=0 + deposit_waived=true (เพื่อ audit)
          deposit_amount: item.deposit_waived ? 0 : item.deposit_amount,
          deposit_waived: item.deposit_waived, // 🐛 ต้องส่งให้ BE persist (เดิม edit ไม่ส่ง → reset เป็น false ทุกครั้งที่แก้)
          is_software_only: item.is_software_only, // เช่า Software อย่างเดียว
          notes: item.notes,
          // New fields for data migration - deposit tracking
          // Deposit v2 invariants: I1 returned_date ⇔ RETURNED · I2 PENDING/WAIVED ⇒ paid_date null
          // (ช่องวันที่ถูก disable ตามสถานะอยู่แล้ว แต่ค่าเก่าอาจค้างใน state → ล้างก่อนส่ง กัน 400)
          deposit_status: item.deposit_status || 'PENDING',
          deposit_paid_date:
            item.deposit_paid_date && item.deposit_status !== 'PENDING' && item.deposit_status !== 'WAIVED'
              ? new Date(item.deposit_paid_date)
              : null,
          deposit_returned_date:
            item.deposit_returned_date && item.deposit_status === 'RETURNED'
              ? new Date(item.deposit_returned_date)
              : null,
          // Deposit v2: hold มีผลเฉพาะ PENDING_REFUND (invariant I3) — สถานะอื่นส่ง false/null ให้ backend ล้าง
          deposit_hold: item.deposit_status === 'PENDING_REFUND' ? !!item.deposit_hold : false,
          deposit_hold_note:
            item.deposit_status === 'PENDING_REFUND' && item.deposit_hold
              ? (item.deposit_hold_note?.trim() || null)
              : null,
          // Remote Access (AnyDesk) — REPLACE STRATEGY: ต้องส่ง id + notes กลับเสมอไม่งั้นค่าหาย
          anydesk_id: item.anydesk_id?.trim() || null,
          remote_access_notes: item.remote_access_notes?.trim() || null,
        }))
      };
      
      
      // S1230: opt-in optimistic-concurrency via If-Match (OBS-S1223-001 stack)
      // pass updated_at จาก initial read · BE จะ return 412 ถ้ามีคนแก้ระหว่างที่ฟอร์มเปิดอยู่
      // BUG-S215-IFMATCH-NULL: rental ที่ยังไม่เคยถูกแก้มี updated_at = null → เดิมไม่ส่ง
      // header เลย = optimistic lock ปิดตัวเองเงียบๆ ในจังหวะที่เสี่ยงที่สุด (ใบร่างใหม่
      // ที่อีกแท็บกำลังกดอนุมัติ) · fallback ไป created_at ซึ่งมีทุกแถวและไม่เปลี่ยน
      const ifMatch = originalRentalData?.updated_at || originalRentalData?.created_at;
      if (recordId === null) return; // BUG-S2283-001: ไม่ยิง PUT /rentals/NaN
      await rentalService.updateRental(recordId, rentalData, ifMatch);

      showAlert('สำเร็จ', 'บันทึกข้อมูลการเช่าเรียบร้อยแล้ว', 'success');
      setIsFormDirty(false); // ปิดการเตือนก่อน reload

      // รีโหลดข้อมูลใหม่เพื่อแสดงค่าที่อัพเดตแล้ว
      setLoading(false); // ปิด loading ก่อน reload
      await loadRentalData();

      // ถ้าต้องการกลับไปหน้า list ให้ uncomment บรรทัดนี้
      // navigate('/rentals/list');

    } catch (error: any) {
      console.error('Error updating rental:', error);
      // S1230: dedicated handling for 412 Precondition Failed (stale write)
      if (error.response?.status === 412) {
        // BUG-FE-RENT-EDIT-MSG FIX: showAlert เป็น 3-arg (title, message, type) —
        // เดิมส่ง 2-arg ทำให้ 'warning' ไปโผล่เป็น body และ type กลายเป็น info
        showAlert(
          'แจ้งเตือน',
          error.response?.data?.error?.user_message ||
            'ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น กรุณาโหลดข้อมูลใหม่แล้วลองอีกครั้ง',
          'warning'
        );
        // โหลดข้อมูลใหม่อัตโนมัติเพื่อให้ user เห็น state ปัจจุบัน
        setLoading(false);
        await loadRentalData();
        return;
      }
      // BUG-FE-RENT-EDIT-MSG FIX: 3-arg + อ่าน KRP envelope ให้ถูก path
      // (data.error.user_message/detail — เดิมอ่าน data.message ที่ไม่มีจริง)
      // BUG-S2158-001: อ่าน field_errors ก่อน user_message (ประโยคกลางของทุก 422) — ดู RentalCreatePageV2
      showAlert(
        'เกิดข้อผิดพลาด',
        getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // [PO 2026-06-24] สรุปยอดล่างสุดแสดง "ค่าเช่ารวม/เดือน" (= monthlyRentalValue) แทนค่ามัดจำ
  // (มัดจำมี panel ด้านบนแล้ว). totalDeposit ยังใช้คำนวณ deposit-impact ของ panel มัดจำด้านบน
  const { totalDeposit, monthlyRentalValue } = calculateTotals();

  // คำนวณผลกระทบต่อวงเงินมัดจำ (advisory — live)
  //
  // กัน DOUBLE-COUNT ของสัญญาตัวเอง:
  //   total_deposit (จาก getTotalDeposit) = SUM(deposit_amount) ของ rental "DRAFT+ACTIVE" ทุกตัวของบริษัท
  //   ซึ่ง "รวมสัญญาที่กำลังแก้นี้อยู่แล้ว" (มันอยู่ใน active set). ถ้าเอา total_deposit + ยอด form
  //   ตรงๆ จะนับมัดจำของสัญญานี้ซ้ำสองรอบ.
  //   → used (live) = total_deposit − (มัดจำเดิมของสัญญานี้ = originalRentalData.deposit_amount)
  //                                  + (ผลรวมมัดจำ item ในฟอร์มที่ "ไม่ติ๊กฟรี" = totalDeposit)
  //   มัดจำเดิม originalRentalData.deposit_amount = rental.deposit_amount (aggregate ที่ BE เก็บไว้
  //   ตอน save ล่าสุด = ผลรวมมัดจำ item ที่ไม่ถูกยกเว้น) → หักออกพอดีกับส่วนที่ total_deposit นับให้
  const calculateDepositImpact = () => {
    if (!companyDepositInfo || !originalRentalData) return null;

    // deposit_limit = 0 หมายถึง "ไม่จำกัดวงเงิน"
    const hasLimit = companyDepositInfo.deposit_limit > 0;
    const originalDeposit = originalRentalData.deposit_amount || 0;
    // ใช้ไป (live) — หักมัดจำเดิมของสัญญานี้ออกก่อน แล้วบวกยอดฟอร์มปัจจุบัน (กันนับซ้ำ)
    const newTotalDeposit = companyDepositInfo.total_deposit - originalDeposit + totalDeposit;
    const remainingAfter = hasLimit ? companyDepositInfo.deposit_limit - newTotalDeposit : Infinity;
    const usageAfter = hasLimit ? (newTotalDeposit / companyDepositInfo.deposit_limit) * 100 : 0;

    return {
      newTotalDeposit,
      remainingAfter,
      usageAfter,
      isOverLimit: hasLimit && newTotalDeposit > companyDepositInfo.deposit_limit,
      isNearLimit: hasLimit && usageAfter >= 90
    };
  };

  const depositImpact = calculateDepositImpact();

  // ค่าที่ Panel "วงเงินมัดจำ" ใช้แสดง (live) — มาจาก depositImpact เพื่อกัน double-count
  // ถ้ายังไม่มี depositImpact (เช่น originalRentalData ยังไม่โหลด) ให้ fallback เป็นค่า server
  const depositLimit = companyDepositInfo?.deposit_limit ?? 0;
  const liveUsedDeposit = depositImpact ? depositImpact.newTotalDeposit : (companyDepositInfo?.total_deposit ?? 0);
  const liveRemainingDeposit = depositLimit > 0 ? depositLimit - liveUsedDeposit : 0;
  const liveUsagePercentage = depositImpact && depositLimit > 0
    ? depositImpact.usageAfter
    : (companyDepositInfo?.usage_percentage ?? 0);

  // 1 เครื่อง = 1 สัญญา (มติ 10 ต.ค. 2026) — เตือนอย่างเดียว ไม่บล็อกบันทึก · สัญญาเก่าที่รวมหลายเครื่อง
  // ยังแก้ไข/บันทึกได้ตามปกติ (เตือนเฉพาะเมื่อ > 1)
  const serializedDeviceCount = countSerializedDevices(formData.rental_items, devices);

  if (pageLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmModal
        isOpen={showModal}
        onClose={onCancelLeave}
        onConfirm={onConfirmLeave}
        pushHistoryOnOpen={false}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่แก้ไขจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <div className="w-full">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiEditLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">แก้ไขรายการเช่า</h1>
              <p className="text-xs text-secondary-500 mt-0.5">จัดการและปรับปรุงข้อมูลรายการเช่าอุปกรณ์</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <RiFileTextLine className="h-4 w-4 mr-1.5" />
              ดูหมายเหตุ
            </button>
            <button
              type="button"
              onClick={() => {
                // Preserve list state (page/filter) by going back in history.
                // Fallback to /rentals/list if there's no history (e.g., opened from direct URL)
                if (window.history.length > 1) navigate(-1);
                else navigate('/rentals/list');
              }}
              className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <RiArrowLeftLine className="h-4 w-4 mr-1.5" />
              กลับ
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} onChange={() => setIsFormDirty(true)} noValidate className="space-y-6">
          {/* ข้อมูลทั่วไป */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiInformationLine className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-semibold text-secondary-800">ข้อมูลทั่วไป</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-6">
                {/* เลขที่การเช่า */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="rental_number" className="block text-sm font-medium text-secondary-700">
                    เลขที่การเช่า *
                  </label>
                  <input
                    type="text"
                    id="rental_number"
                    value={formData.rental_number}
                    readOnly
                    className="input mt-1 bg-secondary-50 text-secondary-500 cursor-not-allowed"
                  />
                </div>

                {/* บริษัท */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="company_id" className="block text-sm font-medium text-secondary-700">
                    บริษัท *
                  </label>
                  <SearchableSelect
                    options={companies.map(company => ({
                      value: company.id,
                      label: `${company.code} - ${company.name}`
                    }))}
                    value={formData.company_id || ''}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        company_id: Number(value),
                        site_id: '' // reset site เมื่อเปลี่ยนบริษัท
                      }));
                      if (formErrors.company_id) {
                        setFormErrors(prev => ({ ...prev, company_id: '' }));
                      }
                    }}
                    placeholder="-- เลือกบริษัท --"
                    searchPlaceholder="ค้นหาบริษัท..."
                    disabled={siteLocked}
                  />
                  {/* siteLocked: เปลี่ยนบริษัทจะ reset site_id เป็นค่าว่าง แต่ dropdown โครงการ
                      ถูกล็อค → ผู้ใช้ติดตาย จึงล็อคบริษัทพร้อมกัน */}
                  {formErrors.company_id && (
                    <p className="mt-1 text-sm text-danger-600">{formErrors.company_id}</p>
                  )}
                </div>

                {/* โครงการ */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="site_id" className="block text-sm font-medium text-secondary-700">
                    โครงการ *
                  </label>
                  <SearchableSelect
                    options={filteredSites.map(site => ({
                      value: site.id,
                      label: `${site.code} - ${site.name}`
                    }))}
                    value={formData.site_id || ''}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, site_id: Number(value) }));
                      if (formErrors.site_id) {
                        setFormErrors(prev => ({ ...prev, site_id: '' }));
                      }
                    }}
                    placeholder="-- เลือกโครงการ --"
                    searchPlaceholder="ค้นหาโครงการ..."
                    disabled={!formData.company_id || siteLocked}
                  />
                  {siteLocked && (
                    <p className="mt-1 text-xs text-secondary-500">
                      เปลี่ยนโครงการได้ผ่านเมนู ย้ายอุปกรณ์ เท่านั้น
                    </p>
                  )}
                  {formErrors.site_id && (
                    <p className="mt-1 text-sm text-danger-600">{formErrors.site_id}</p>
                  )}
                </div>

                {/* วันที่เช่า */}
                <div className="sm:col-span-1 lg:col-span-3">
                  <label htmlFor="rental_date" className="block text-sm font-medium text-secondary-700">
                    วันที่เช่า *
                  </label>
                  <input
                    type="date"
                    id="rental_date"
                    value={formData.rental_date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, rental_date: e.target.value }));
                      if (formErrors.rental_date) {
                        setFormErrors(prev => ({ ...prev, rental_date: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.rental_date && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    aria-invalid={Boolean(formErrors.rental_date)}
                    aria-describedby={formErrors.rental_date ? 'rental_date-error' : undefined}
                  />
                  {formErrors.rental_date && (
                    <p id="rental_date-error" className="mt-1 text-sm text-danger-600">{formErrors.rental_date}</p>
                  )}
                </div>

                {/* วันที่คาดว่าจะครบสัญญา (edit: ไม่บังคับ — record เก่า NULL ต้อง save ได้) */}
                <div className="sm:col-span-1 lg:col-span-3">
                  <label htmlFor="expected_return_date" className="block text-sm font-medium text-secondary-700">
                    วันที่คาดว่าจะครบสัญญา
                  </label>
                  <input
                    type="date"
                    id="expected_return_date"
                    value={formData.expected_return_date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, expected_return_date: e.target.value }));
                      if (formErrors.expected_return_date) {
                        setFormErrors(prev => ({ ...prev, expected_return_date: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.expected_return_date && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    aria-invalid={Boolean(formErrors.expected_return_date)}
                    aria-describedby={formErrors.expected_return_date ? 'expected_return_date-error' : undefined}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    สัญญาเปิด — เช่าต่อเนื่องจนกว่าจะแจ้งยกเลิก (ไม่ต้องระบุก็ได้)
                  </p>
                  {formErrors.expected_return_date && (
                    <p id="expected_return_date-error" className="mt-1 text-sm text-danger-600">{formErrors.expected_return_date}</p>
                  )}
                </div>

                {/* วันที่เริ่มเก็บเงิน */}
                <div className="sm:col-span-1 lg:col-span-3">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    วันที่เริ่มเก็บเงิน <span className="text-secondary-500 font-normal">(ไม่บังคับ)</span>
                  </label>
                  <div className="relative">
                    <RiCalendarLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                    <input
                      type="date"
                      name="billing_start_date"
                      aria-label="วันที่เริ่มเก็บเงิน"
                      value={formData.billing_start_date}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, billing_start_date: e.target.value }));
                        if (formErrors.billing_start_date) {
                          setFormErrors(prev => ({ ...prev, billing_start_date: '' }));
                        }
                      }}
                      min={formData.rental_date}
                      className={cn('input pl-10', formErrors.billing_start_date && 'border-danger-500 bg-danger-50')}
                      aria-invalid={Boolean(formErrors.billing_start_date)}
                      aria-describedby={formErrors.billing_start_date ? 'billing_start_date-error' : undefined}
                    />
                  </div>
                  {formErrors.billing_start_date && (
                    <p id="billing_start_date-error" className="mt-1 text-sm text-danger-600">{formErrors.billing_start_date}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    หากมีช่วงทดลองใช้ฟรี ให้ระบุวันที่เริ่มเก็บเงินจริง
                  </p>
                </div>

                {/* วันสิ้นสุดเก็บเงิน */}
                <div className="sm:col-span-1 lg:col-span-3">
                  <label htmlFor="billing_end_date" className="block text-sm font-medium text-secondary-700">
                    วันสิ้นสุดเก็บเงิน
                    <span
                      title="วันสุดท้ายที่ระบบจะเรียกเก็บเงิน ใช้คำนวณ proration ในใบแจ้งหนี้"
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary-200 text-secondary-500 text-xs cursor-help"
                    >
                      ?
                    </span>
                  </label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <RiCalendarLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5 pointer-events-none" />
                      <input
                        type="date"
                        id="billing_end_date"
                        name="billing_end_date"
                        aria-label="วันสิ้นสุดเก็บเงิน"
                        value={formData.billing_end_date}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, billing_end_date: e.target.value }));
                        }}
                        className={cn('input pl-10 w-full', loading && 'bg-secondary-50 cursor-not-allowed')}
                        disabled={loading}
                      />
                    </div>
                    {formData.billing_end_date && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, billing_end_date: '' }))}
                        className="px-3 py-2 text-xs font-medium text-secondary-600 bg-secondary-100 border border-secondary-300 rounded-lg hover:bg-secondary-200 transition-colors whitespace-nowrap"
                        title="ล้างค่า — ระบบจะเรียกเก็บเงินต่อเนื่อง"
                        disabled={loading}
                      >
                        ล้างค่า
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-secondary-500">
                    ถ้าไม่กำหนด ระบบจะเรียกเก็บเงินต่อเนื่อง (ongoing)
                  </p>
                </div>

                {/* ชำระล่วงหน้าถึงวันที่ — วางบิลข้ามรอบที่ลูกค้าจ่ายแล้ว */}
                <PrepaidUntilFields
                  prepaidUntil={formData.prepaid_until}
                  prepaidNote={formData.prepaid_note}
                  minDate={formData.billing_start_date || formData.rental_date}
                  disabled={loading}
                  error={formErrors.prepaid_until}
                  onChange={(next) => {
                    setFormData(prev => ({ ...prev, ...next }));
                    if (formErrors.prepaid_until) setFormErrors(prev => ({ ...prev, prepaid_until: '' }));
                  }}
                />

                {/* ระยะเวลา (วัน) — แสดงเฉพาะเมื่อระบุวันคืน */}
                {formData.expected_return_date && (
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="rental_period_days" className="block text-sm font-medium text-secondary-700">
                    ระยะเวลา (วัน)
                  </label>
                  <input
                    type="number"
                    id="rental_period_days"
                    value={formData.rental_period_days}
                    readOnly
                    className="input mt-1 bg-secondary-50 text-secondary-500"
                  />
                  <p className="mt-1 text-sm text-secondary-500">คำนวณอัตโนมัติจากวันที่เช่าและวันที่คืน</p>
                </div>
                )}

                {/* แสดงข้อมูล Free Trial ถ้ามี */}
                {formData.billing_start_date && formData.rental_date &&
                 new Date(formData.billing_start_date) > new Date(formData.rental_date) && (
                  <div className="sm:col-span-2 lg:col-span-6 bg-info-50 border border-info-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-info-700">
                      <RiGiftLine className="w-5 h-5" />
                      <span className="font-medium">
                        ทดลองใช้ฟรี {Math.ceil((new Date(formData.billing_start_date).getTime() - new Date(formData.rental_date).getTime()) / (1000 * 60 * 60 * 24))} วัน
                      </span>
                    </div>
                  </div>
                )}

                {/* วันที่ส่งมอบอุปกรณ์ */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="delivery_date" className="block text-sm font-medium text-secondary-700">
                    วันที่ส่งมอบอุปกรณ์
                  </label>
                  <input
                    type="date"
                    id="delivery_date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                    className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
                    disabled={loading}
                  />
                  <p className="mt-1 text-xs text-secondary-500">วันที่ส่งมอบอุปกรณ์ให้ลูกค้า</p>
                </div>

                {/* หมายเหตุ */}
                <div className="sm:col-span-2 lg:col-span-6">
                  <label htmlFor="notes" className="block text-sm font-medium text-secondary-700">
                    หมายเหตุ
                  </label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, notes: e.target.value }));
                      if (formErrors.notes) {
                        setFormErrors(prev => ({ ...prev, notes: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.notes && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    placeholder="หมายเหตุเพิ่มเติม..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Auto-Renewal Settings Section */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                    <RiListCheck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-secondary-800">ตั้งค่าการต่อสัญญาอัตโนมัติ</h2>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_renew}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, auto_renew: e.target.checked }));
                        if (formErrors.auto_renew) {
                          setFormErrors(prev => ({ ...prev, auto_renew: '' }));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    <span className="ml-3 text-sm font-medium text-secondary-700">
                      {formData.auto_renew ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                  </label>
                </div>
              </div>

              {formData.auto_renew && (
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-6 mt-4 p-4 bg-success-50 rounded-lg border border-success-200">
                  {/* วันสิ้นสุดสัญญา */}
                  <div className="sm:col-span-1 lg:col-span-2">
                    <label htmlFor="end_date" className="block text-sm font-medium text-secondary-700">
                      วันสิ้นสุดสัญญา
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      value={formData.end_date}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, end_date: e.target.value }));
                        if (formErrors.end_date) {
                          setFormErrors(prev => ({ ...prev, end_date: '' }));
                        }
                      }}
                      className="input mt-1"
                    />
                    <p className="mt-1 text-xs text-secondary-500">วันที่สัญญาจะหมดอายุและต่ออัตโนมัติ</p>
                  </div>

                  {/* จำนวนวันแจ้งเตือนก่อนต่อสัญญา */}
                  <div className="sm:col-span-1 lg:col-span-2">
                    <label htmlFor="renewal_notice_days" className="block text-sm font-medium text-secondary-700">
                      แจ้งเตือนล่วงหน้า (วัน)
                    </label>
                    <input
                      type="number"
                      id="renewal_notice_days"
                      min="1"
                      max="90"
                      value={formData.renewal_notice_days}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, renewal_notice_days: Number(e.target.value) }));
                        if (formErrors.renewal_notice_days) {
                          setFormErrors(prev => ({ ...prev, renewal_notice_days: '' }));
                        }
                      }}
                      className={`input mt-1 ${formErrors.renewal_notice_days ? 'border-danger-500' : ''}`}
                      aria-invalid={Boolean(formErrors.renewal_notice_days)}
                      aria-describedby={formErrors.renewal_notice_days ? 'renewal_notice_days-error' : undefined}
                    />
                    {formErrors.renewal_notice_days ? (
                      <p id="renewal_notice_days-error" className="mt-1 text-sm text-danger-600">{formErrors.renewal_notice_days}</p>
                    ) : (
                      <p className="mt-1 text-xs text-secondary-500">ระบบจะแจ้งเตือนก่อนต่อสัญญากี่วัน (1–90 วัน)</p>
                    )}
                  </div>

                  {/* เงื่อนไขการต่อสัญญา */}
                  <div className="sm:col-span-2 lg:col-span-6">
                    <label htmlFor="renewal_terms" className="block text-sm font-medium text-secondary-700">
                      เงื่อนไขการต่อสัญญา
                    </label>
                    <textarea
                      id="renewal_terms"
                      rows={3}
                      value={formData.renewal_terms}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, renewal_terms: e.target.value }));
                        if (formErrors.renewal_terms) {
                          setFormErrors(prev => ({ ...prev, renewal_terms: '' }));
                        }
                      }}
                      className="input mt-1"
                      placeholder="ระบุเงื่อนไขพิเศษสำหรับการต่อสัญญา..."
                    />
                    <p className="mt-1 text-xs text-secondary-500">เงื่อนไขหรือข้อตกลงพิเศษสำหรับการต่อสัญญาอัตโนมัติ</p>
                  </div>
                </div>
              )}

              {!formData.auto_renew && (
                <div className="text-sm text-secondary-500 mt-2">
                  <p>เปิดใช้งานเพื่อให้ระบบต่อสัญญาอัตโนมัติเมื่อครบกำหนด และแจ้งเตือนลูกค้าล่วงหน้า</p>
                </div>
              )}
            </div>
          </div>

          {/* แสดงข้อมูลค่ามัดจำรวมของบริษัท */}
          {formData.company_id && companyDepositInfo && (
            <div className="bg-info-50 border border-info-200 rounded-lg shadow-sm mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center gap-2 pb-2 border-b border-info-200 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-sm">
                    <RiWalletLine className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-blue-900">
                    สรุปค่ามัดจำของบริษัท: {companies.find(c => c.id === formData.company_id)?.name}
                  </h3>
                </div>
                {/* ค่าใน Panel เป็น LIVE (กัน double-count สัญญาตัวเอง) — ดู calculateDepositImpact */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600">ค่ามัดจำที่ใช้ไป (รวมรายการนี้)</p>
                    <p className={cn(
                      'text-2xl font-bold',
                      depositLimit > 0 && liveUsedDeposit > depositLimit ? 'text-danger-600' : 'text-blue-600'
                    )}>
                      {liveUsedDeposit.toLocaleString()} บาท
                    </p>
                    <p className="text-xs text-secondary-500 mt-1">
                      จาก {companyDepositInfo.active_rental_count} รายการเช่าที่ใช้งานอยู่ (รวมรายการที่กำลังแก้)
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600">วงเงินมัดจำสูงสุด</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {depositLimit > 0 ? `${depositLimit.toLocaleString()} บาท` : 'ไม่จำกัด'}
                    </p>
                    {depositLimit > 0 && (
                      <div className="mt-2">
                        <div className="bg-secondary-200 rounded-full h-2">
                          <div
                            className={cn(
                              'h-2 rounded-full',
                              liveUsagePercentage >= 100 ? 'bg-danger-500' :
                              liveUsagePercentage >= 90 ? 'bg-warning-500' :
                              'bg-success-500'
                            )}
                            style={{ width: `${Math.min(liveUsagePercentage, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-secondary-500 mt-1">
                          ใช้ไปแล้ว {liveUsagePercentage.toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600">วงเงินคงเหลือ</p>
                    <p className={`text-2xl font-bold ${
                      depositLimit === 0 ? 'text-success-600' :
                      liveRemainingDeposit >= 0 ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {depositLimit === 0 ? 'ไม่จำกัด' : `${liveRemainingDeposit.toLocaleString()} บาท`}
                    </p>
                    <p className="text-xs text-secondary-500 mt-1">
                      {depositLimit === 0
                        ? 'ไม่จำกัดวงเงิน'
                        : liveRemainingDeposit >= 0
                          ? 'ยังอยู่ในวงเงิน'
                          : 'เกินวงเงิน (เตือน — ไม่บล็อก)'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* รายการอุปกรณ์ */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                    <RiListCheck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-secondary-800">
                    รายการอุปกรณ์
                    <span className="ml-2 text-sm text-secondary-500 font-normal">
                      (อุปกรณ์พร้อมใช้: {availableDevices.length})
                    </span>
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addRentalItem}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 min-h-[44px] sm:min-h-[36px] rounded-md text-sm font-medium flex items-center"
                >
                  <RiAddLine className="h-4 w-4 mr-2" />
                  เพิ่มรายการ
                </button>
              </div>

              {serializedDeviceCount > 1 && (
                <div
                  role="status"
                  className="mb-4 flex items-start gap-2 bg-warning-50 border border-warning-200 rounded-lg p-3"
                >
                  <RiAlertLine className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warning-800">{MULTI_SERIALIZED_WARNING}</p>
                </div>
              )}

              <div className="space-y-6">
                {formData.rental_items.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-slate-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-medium text-secondary-900">
                        รายการที่ {index + 1}
                      </h3>
                      {formData.rental_items.length > 1 && (
                        // BUG-S083-DELTARGET (same button as the create page): 20×20 destructive
                        // icon with no accessible name — padded to 44px and labelled.
                        <button
                          type="button"
                          onClick={() => removeRentalItem(index)}
                          aria-label={`ลบรายการที่ ${index + 1}`}
                          title={`ลบรายการที่ ${index + 1}`}
                          className="inline-flex items-center justify-center h-11 w-11 -mr-2 rounded-md text-danger-600 hover:text-red-800 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-danger-500"
                        >
                          <RiDeleteBinLine className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-6">
                      {/* GAP-29: สินค้า (Product) */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          สินค้า
                        </label>
                        <SearchableSelect
                          options={products.map(product => ({
                            value: product.id,
                            label: `${product.code} - ${product.name}`
                          }))}
                          value={item.product_id || ''}
                          onChange={(value) => {
                            updateRentalItem(index, 'product_id', value ? Number(value) : '');
                            // ถ้าสินค้ามีค่าเช่าเริ่มต้น ให้อัพเดตค่าเช่าต่อเดือน
                            const selectedProduct = products.find(p => p.id === Number(value));
                            if (selectedProduct && selectedProduct.rental_rate) {
                              updateRentalItem(index, 'rental_rate_per_month', selectedProduct.rental_rate);
                            }
                          }}
                          placeholder="-- เลือกสินค้า --"
                          searchPlaceholder="ค้นหาสินค้า..."
                        />
                      </div>

                      {/* Checkbox Software Only */}
                      <div className="sm:col-span-2 lg:col-span-4 flex items-end pb-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`sw_only_${index}`}
                            checked={item.is_software_only || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updatedItems = [...formData.rental_items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                is_software_only: checked,
                                ...(checked
                                  ? {
                                      _prev_device_type_id: updatedItems[index].device_type_id,
                                      _prev_device_id: updatedItems[index].device_id,
                                      device_type_id: '',
                                      device_id: ''
                                    }
                                  : {
                                      device_type_id: updatedItems[index]._prev_device_type_id || '',
                                      device_id: updatedItems[index]._prev_device_id || ''
                                    })
                              };
                              setFormData(prev => ({ ...prev, rental_items: updatedItems }));
                            }}
                            className="w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500 cursor-pointer"
                          />
                          <label htmlFor={`sw_only_${index}`} className="text-sm font-medium text-secondary-700 cursor-pointer select-none">
                            เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
                          </label>
                          {item.is_software_only && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                              SW Only
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ประเภทอุปกรณ์ + อุปกรณ์ (ซ่อนเมื่อเป็น SW Only) */}
                      {!item.is_software_only && (
                        <>
                      {/* ประเภทอุปกรณ์ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ประเภทอุปกรณ์ *
                        </label>
                        <SearchableSelect
                          options={deviceTypes.map(deviceType => ({
                            value: deviceType.id,
                            label: `${deviceType.code} - ${deviceType.name}`
                          }))}
                          value={item.device_type_id || ''}
                          onChange={(value) => updateRentalItem(index, 'device_type_id', Number(value))}
                          placeholder={loadingDeviceTypes ? "กำลังโหลดประเภทอุปกรณ์..." : "-- เลือกประเภทอุปกรณ์ --"}
                          searchPlaceholder="ค้นหาประเภทอุปกรณ์..."
                          disabled={loadingDeviceTypes}
                        />
                        {/* BUG-178 FIX: per-field loading indicator */}
                        {loadingDeviceTypes && (
                          <p className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            กำลังโหลดข้อมูล...
                          </p>
                        )}
                        {formErrors[`item_${index}_device_type`] && (
                          <p className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_device_type`]}</p>
                        )}
                      </div>

                      {/* อุปกรณ์ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          อุปกรณ์ *
                        </label>
                        <SearchableSelect
                          options={devicesForItem(
                              availableDevices,
                              item.device_type_id,
                              formData.rental_items.map(it => it.device_id),
                            )
                            .map(device => ({
                              value: device.id,
                              // R3U-06: helper กลาง — ของนับจำนวนไม่มี S/N ห้ามโชว์ "S/N: null"
                              // P-S475-1: เตือนเครื่องที่มีเคสซ่อมค้างก่อนเลือกส่งมอบ
                              // fix ค: เครื่องที่ยังผูกกับสัญญาอื่นอยู่ต้องเห็นก่อนเลือก — ส่ง `id` (สัญญาที่กำลัง
                              // แก้ไขอยู่) กันไม่ให้เตือนเครื่องของสัญญานี้เอง (เช่นสัญญา DRAFT ที่ยังไม่อนุมัติ)
                              label: withRentalHoldLabel(
                                deviceOptionLabel(device, {
                                  openMaintenanceCount: (device as any).open_maintenance_count,
                                }),
                                device,
                                id
                              ),
                            }))}
                          value={item.device_id || ''}
                          onChange={(value) => updateRentalItem(index, 'device_id', Number(value))}
                          placeholder={loadingDevices ? "กำลังโหลดอุปกรณ์..." : !item.device_type_id ? "-- กรุณาเลือกประเภทอุปกรณ์ก่อน --" : "-- เลือกอุปกรณ์ --"}
                          searchPlaceholder="ค้นหาอุปกรณ์..."
                          disabled={!item.device_type_id || loadingDevices}
                        />
                        {/* BUG-178 FIX: per-field loading indicator */}
                        {loadingDevices && (
                          <p className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            กำลังโหลดข้อมูล...
                          </p>
                        )}
                        {formErrors[`item_${index}_device`] && (
                          <p className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_device`]}</p>
                        )}
                        {/* fix ค: เครื่องที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
                        {(() => {
                          const selectedDevice = devices.find(d => d.id === item.device_id);
                          const hold = rentalHoldFor(selectedDevice, id);
                          if (!hold) return null;
                          return (
                            <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                              {hold.message}
                            </p>
                          );
                        })()}
                      </div>
                        </>
                      )}

                      {/* ซิมการ์ด */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ซิมการ์ด (ถ้ามี)
                        </label>
                        {(() => {
                          return null;
                        })()}
                        <SearchableSelect
                          options={availableSims.map(sim => {
                            const base = {
                              value: sim.id,
                              // R4U-3: helper กลาง — ซิมที่ operator ว่างห้ามโชว์ "เบอร์ - null (รายเดือน)"
                              label: simOptionLabel(sim)
                            };
                            // fix ค: ซิมที่ยังผูกกับสัญญาอื่นอยู่ต้องเห็นก่อนเลือก (คงไว้เฉพาะซิมของสัญญานี้เอง)
                            return { ...base, label: withRentalHoldLabel(base.label, sim, id) };
                          })}
                          value={item.sim_id || ''}
                          onChange={(value) => {
                            updateRentalItem(index, 'sim_id', value ? Number(value) : '');
                          }}
                          placeholder="-- เลือกซิมการ์ด --"
                          searchPlaceholder="ค้นหาเบอร์โทรศัพท์..."
                        />
                        <p className="mt-1 text-sm text-secondary-500">
                          เลือกซิมการ์ดสำหรับอุปกรณ์นี้ (ถ้าต้องการ)
                          {item.sim_id && <span className="text-blue-600"> | ปัจจุบัน: SIM ID {item.sim_id}</span>}
                        </p>
                        {/* fix ค: ซิมที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
                        {(() => {
                          const selectedSim = sims.find(s => s.id === item.sim_id);
                          const hold = rentalHoldFor(selectedSim, id);
                          if (!hold) return null;
                          return (
                            <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                              {hold.message}
                            </p>
                          );
                        })()}
                      </div>

                      {/* จำนวน */}
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700">
                          จำนวน
                        </label>
                        <input
                          type="number"
                          min="1"
                          aria-label="จำนวน"
                          value={item.quantity}
                          onChange={(e) => updateRentalItem(index, 'quantity', Number(e.target.value))}
                          className={cn('input mt-1', formErrors[`item_${index}_quantity`] && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                          aria-invalid={Boolean(formErrors[`item_${index}_quantity`])}
                          aria-describedby={formErrors[`item_${index}_quantity`] ? `item_${index}_quantity-error` : undefined}
                        />
                        {/* BUG-FE-QTYERR-SILENT FIX: this key was used ONLY for the red
                            border — its message was computed, stored in formErrors, and
                            never shown. Because validateForm returns false, บันทึก then
                            did nothing with no explanation: no text, and no scroll-to-error
                            either (the scroll looks up [name="..."], and these inputs carry
                            no name attribute). Sibling fields (device_type, device, rate)
                            all render their message; quantity was the odd one out. */}
                        {formErrors[`item_${index}_quantity`] && (
                          <p id={`item_${index}_quantity-error`} className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_quantity`]}</p>
                        )}
                      </div>

                      {/* ค่าเช่าต่อเดือน */}
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700">
                          ค่าเช่าต่อเดือน *
                          <span className="text-secondary-500 text-sm"> - ใส่ 0 ได้</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label="ค่าเช่าต่อเดือน"
                          value={item.rental_rate_per_month}
                          onChange={(e) => updateRentalItem(index, 'rental_rate_per_month', Number(e.target.value))}
                          className={cn('input mt-1', formErrors[`item_${index}_rate`] && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                          aria-invalid={Boolean(formErrors[`item_${index}_rate`])}
                          aria-describedby={formErrors[`item_${index}_rate`] ? `item_${index}_rate-error` : undefined}
                        />
                        {formErrors[`item_${index}_rate`] && (
                          <p id={`item_${index}_rate-error`} className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_rate`]}</p>
                        )}
                      </div>

                      {/* ค่ามัดจำ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ค่ามัดจำ (บาท) <span className="text-secondary-500 text-sm">- สามารถเป็น 0 ได้</span>
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label="ค่ามัดจำ (บาท)"
                            value={item.deposit_amount}
                            onChange={(e) => updateRentalItem(index, 'deposit_amount', Number(e.target.value))}
                            disabled={item.deposit_waived || loading || depositAmountLocked(item)}
                            title={depositAmountLocked(item) ? DEPOSIT_AMOUNT_LOCK_HINT : undefined}
                            className={cn('input', formErrors[`item_${index}_deposit`] && 'border-danger-500', (item.deposit_waived || loading || depositAmountLocked(item)) && 'bg-secondary-50 cursor-not-allowed text-gray-400')}
                          />
                        </div>
                        {depositAmountLocked(item) && (
                          <p className="mt-1 text-xs text-secondary-500">{DEPOSIT_AMOUNT_LOCK_HINT}</p>
                        )}
                        {/* ฟรีค่ามัดจำ — รายเครื่อง (advisory วงเงินต่อบริษัท) */}
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`deposit_waived_${index}`}
                            checked={item.deposit_waived || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const updatedItems = [...formData.rental_items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                deposit_waived: isChecked,
                                ...(isChecked
                                  ? {
                                      // จำค่ามัดจำเดิมไว้ + เซ็ตเป็น 0 (ไม่นับใน "ใช้ไป")
                                      _prev_deposit_amount: updatedItems[index].deposit_amount,
                                      deposit_amount: 0
                                    }
                                  : {
                                      // ยกเลิกติ๊ก → คืนค่ามัดจำเดิม
                                      deposit_amount: updatedItems[index]._prev_deposit_amount || 0
                                    })
                              };
                              setFormData(prev => ({ ...prev, rental_items: updatedItems }));
                              setIsFormDirty(true);
                            }}
                            disabled={loading || depositAmountLocked(item)}
                            className="w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed"
                          />
                          <label
                            htmlFor={`deposit_waived_${index}`}
                            className="text-sm font-medium text-secondary-700 cursor-pointer select-none"
                          >
                            ฟรีค่ามัดจำ <span className="text-secondary-500">(ยกเว้น — ไม่นับในวงเงิน)</span>
                          </label>
                        </div>
                      </div>

                      {/* สถานะมัดจำ — Deposit v2: PENDING_REFUND/RETURNED ที่บันทึกอยู่แล้วต้องไปจัดการในหน้ารายละเอียด
                          (ปุ่มคืน/คง/ปลด/แก้ไขบันทึกคืน มี audit + วันทำการ) · เลือก RETURNED/PENDING_REFUND ใหม่ต้องมี rentals.deposit_manage */}
                      {(() => {
                        const depositLocked =
                          item._original_deposit_status === 'PENDING_REFUND' ||
                          item._original_deposit_status === 'RETURNED';
                        return (
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          สถานะมัดจำ
                        </label>
                        <select
                          value={item.deposit_status}
                          onChange={(e) => updateRentalItem(index, 'deposit_status', e.target.value)}
                          aria-label="สถานะมัดจำ"
                          className={cn('select mt-1', (loading || depositLocked) && 'bg-secondary-50 cursor-not-allowed')}
                          disabled={loading || depositLocked}
                        >
                          <option value="PENDING">รอชำระ</option>
                          <option value="PAID">ชำระแล้ว</option>
                          <option value="PENDING_REFUND" disabled={!canManageDeposit}>รอคืนมัดจำ</option>
                          <option value="RETURNED" disabled={!canManageDeposit}>คืนแล้ว</option>
                          <option value="WAIVED">ยกเว้น</option>
                        </select>
                        {depositLocked && (
                          <p className="mt-1 text-xs text-secondary-500">
                            สถานะนี้แก้จากฟอร์มไม่ได้ —{' '}
                            <a
                              href={`/rentals/view/${id}`}
                              onClick={(e) => { e.preventDefault(); navigate(`/rentals/view/${id}`); }}
                              className="text-primary-600 hover:text-primary-800 underline"
                            >
                              จัดการมัดจำในหน้ารายละเอียด
                            </a>
                          </p>
                        )}
                        {!depositLocked && !canManageDeposit && (
                          <p className="mt-1 text-xs text-secondary-500">
                            "รอคืนมัดจำ" / "คืนแล้ว" ต้องมีสิทธิ์บันทึกรับ/คืน/คงเงินมัดจำ
                          </p>
                        )}
                        {/* คงมัดจำไว้ก่อน — เฉพาะ PENDING_REFUND */}
                        {item.deposit_status === 'PENDING_REFUND' && (
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`deposit_hold_${index}`}
                                checked={item.deposit_hold || false}
                                onChange={(e) => {
                                  updateRentalItem(index, 'deposit_hold', e.target.checked);
                                  setIsFormDirty(true);
                                }}
                                disabled={loading || !canManageDeposit}
                                className="w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed"
                              />
                              <label
                                htmlFor={`deposit_hold_${index}`}
                                className="text-sm font-medium text-secondary-700 cursor-pointer select-none"
                              >
                                ยังไม่รับเงินมัดจำคืน (คงมัดจำไว้ก่อน)
                              </label>
                            </div>
                            {item.deposit_hold && (
                              <input
                                type="text"
                                aria-label={`เหตุผลที่คงมัดจำไว้ รายการที่ ${index + 1}`}
                                value={item.deposit_hold_note}
                                onChange={(e) => updateRentalItem(index, 'deposit_hold_note', e.target.value)}
                                maxLength={500}
                                placeholder="เหตุผล/เงื่อนไขที่คงไว้"
                                className={cn('input', (loading || !canManageDeposit) && 'bg-secondary-50 cursor-not-allowed')}
                                disabled={loading || !canManageDeposit}
                              />
                            )}
                          </div>
                        )}
                      </div>
                        );
                      })()}

                      {/* วันที่โอนมัดจำ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          วันที่โอนมัดจำ
                        </label>
                        <input
                          type="date"
                          aria-label="วันที่โอนมัดจำ"
                          value={item.deposit_paid_date}
                          onChange={(e) => updateRentalItem(index, 'deposit_paid_date', e.target.value)}
                          className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
                          disabled={loading || item.deposit_status === 'PENDING' || item.deposit_status === 'WAIVED'}
                        />
                      </div>

                      {/* วันที่คืนมัดจำ */}
                      {(() => {
                        // Deposit v2: กรอกวันที่คืนได้เฉพาะตอนเปลี่ยนเป็น RETURNED ใหม่ในฟอร์ม + มีสิทธิ์ deposit_manage
                        // (invariant I1 ผูกกับสถานะ) · แถวที่บันทึกคืนไว้แล้ว → แก้วันที่ต้องผ่าน "แก้ไขบันทึกคืน" (reopen +
                        // เหตุผล + audit เก่า→ใหม่) ในหน้ารายละเอียด — ฟอร์มนี้แก้ตรงไม่ได้ (E2E Deposit v2 OBS-7)
                        const alreadyReturned = item._original_deposit_status === 'RETURNED';
                        const returnedDateDisabled =
                          loading || alreadyReturned || item.deposit_status !== 'RETURNED' || !canManageDeposit;
                        return (
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          วันที่คืนมัดจำ
                        </label>
                        <input
                          type="date"
                          aria-label="วันที่คืนมัดจำ"
                          value={item.deposit_returned_date}
                          onChange={(e) => updateRentalItem(index, 'deposit_returned_date', e.target.value)}
                          className={cn('input mt-1', returnedDateDisabled && 'bg-secondary-50 cursor-not-allowed')}
                          disabled={returnedDateDisabled}
                        />
                        {alreadyReturned && (
                          <p className="mt-1 text-xs text-secondary-500">
                            บันทึกคืนไว้แล้ว —{' '}
                            <a
                              href={`/rentals/view/${id}`}
                              onClick={(e) => { e.preventDefault(); navigate(`/rentals/view/${id}`); }}
                              className="text-primary-600 hover:text-primary-800 underline"
                            >
                              แก้ไขบันทึกคืนในหน้ารายละเอียด
                            </a>
                          </p>
                        )}
                      </div>
                        );
                      })()}

                      {/* มูลค่าต่อเดือน */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          มูลค่าต่อเดือน (บาท)
                        </label>
                        <input
                          type="text"
                          aria-label="มูลค่าต่อเดือน (บาท)"
                          value={((item.rental_rate_per_month || 0) * (item.quantity || 1)).toLocaleString()}
                          readOnly
                          className="input mt-1 bg-secondary-50 text-secondary-900 font-medium"
                        />
                        <p className="mt-1 text-sm text-secondary-500">คำนวณจาก: ค่าเช่าต่อเดือน × จำนวน</p>
                      </div>

                      {/* หมายเหตุ */}
                      <div className="sm:col-span-2 lg:col-span-4">
                        <label className="block text-sm font-medium text-secondary-700">
                          หมายเหตุ
                        </label>
                        <input
                          aria-label="หมายเหตุ"
                          type="text"
                          value={item.notes}
                          onChange={(e) => updateRentalItem(index, 'notes', e.target.value)}
                          className={cn('input mt-1', formErrors[`item_${index}_notes`] && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                          placeholder="หมายเหตุสำหรับรายการนี้..."
                        />
                      </div>

                      {/* Remote Access (AnyDesk) */}
                      <div className="sm:col-span-2 lg:col-span-4 mt-2 pt-3 border-t border-secondary-200">
                        <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-secondary-700">
                          <RiRemoteControlLine className="w-4 h-4 text-primary-600" />
                          Remote Access (AnyDesk)
                        </div>
                        {/* PO 2026-09-07: เลิกรับ "รหัสผ่าน AnyDesk" ทั้งฟอร์มสร้างและแก้ไข — เหลือ ID กับหมายเหตุ */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">
                              AnyDesk ID
                            </label>
                            <input
                              type="text"
                              aria-label={`AnyDesk ID รายการที่ ${index + 1}`}
                              value={item.anydesk_id}
                              onChange={(e) => updateRentalItem(index, 'anydesk_id', e.target.value)}
                              className={cn('input mt-1 font-mono', loading && 'bg-secondary-50 cursor-not-allowed')}
                              placeholder="เช่น 123 456 789"
                              maxLength={50}
                              disabled={loading}
                            />
                            {item._anydesk_cleared && (
                              <p className="text-xs text-warning-700 mt-1" data-testid={`anydesk-cleared-note-${index}`}>
                                เปลี่ยนเครื่องแล้ว — ล้าง AnyDesk ของเครื่องเดิมออกให้ กรอกของเครื่องใหม่ได้เลย
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">
                              หมายเหตุ Remote Access
                            </label>
                            <input
                              type="text"
                              aria-label={`หมายเหตุ Remote Access รายการที่ ${index + 1}`}
                              value={item.remote_access_notes}
                              onChange={(e) => updateRentalItem(index, 'remote_access_notes', e.target.value)}
                              className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
                              placeholder="เช่น เครื่องอยู่หน้าเคาน์เตอร์"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* สรุปยอดรวม */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-info-500 to-info-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiCalculatorLine className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-secondary-800">สรุปยอดรวม</h3>
              </div>
              
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-1 lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700">
                    ค่าเช่ารวม (ต่อเดือน)
                  </label>
                  <div className="mt-1 text-2xl font-bold text-primary-600">
                    {monthlyRentalValue.toLocaleString()} บาท/เดือน
                  </div>
                  <div className="text-xs text-secondary-500 mt-1">
                    รวมค่าเช่าทุกอุปกรณ์ต่อเดือน · ยอดเรียกเก็บจริงคิดตามจริงตอนออกใบแจ้งหนี้
                  </div>
                </div>
              </div>

              {/* แสดงข้อมูลเพิ่มเติม */}
              <div className="mt-6">
                <div className="text-sm text-secondary-500">
                  <p>วงเงินมัดจำสูงสุดของบริษัท: {depositSettings.deposit_amount.toLocaleString()} บาท</p>
                  {(() => {
                    const waivedCount = formData.rental_items.filter(item => item.deposit_waived).length;
                    return waivedCount > 0 ? (
                      <p className="mt-1 text-purple-600">
                        รายการที่ได้รับสิทธิ์ฟรีค่ามัดจำ: {waivedCount} รายการ (ไม่นับในวงเงิน)
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* แสดงผลกระทบต่อวงเงินมัดจำ */}
              {depositImpact && totalDeposit > 0 && (
                <div className={`mt-6 p-4 rounded-lg border ${
                  depositImpact.isOverLimit ? 'bg-danger-50 border-danger-200' :
                  depositImpact.isNearLimit ? 'bg-orange-50 border-orange-200' :
                  'bg-info-50 border-info-200'
                }`}>
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 ${
                      depositImpact.isOverLimit ? 'text-danger-600' :
                      depositImpact.isNearLimit ? 'text-warning-600' :
                      'text-blue-600'
                    }`}>
                      {depositImpact.isOverLimit ? <RiForbid2Line className="w-5 h-5" /> : depositImpact.isNearLimit ? <RiAlertLine className="w-5 h-5" /> : <RiInformationLine className="w-5 h-5" />}
                    </div>
                    <div className="ml-3">
                      <h4 className={`text-sm font-medium ${
                        depositImpact.isOverLimit ? 'text-red-800' :
                        depositImpact.isNearLimit ? 'text-orange-800' :
                        'text-blue-800'
                      }`}>
                        {depositImpact.isOverLimit ? 'เกินวงเงินมัดจำ' :
                         depositImpact.isNearLimit ? 'ใกล้เต็มวงเงินมัดจำ' :
                         'ผลกระทบต่อวงเงินมัดจำ'}
                      </h4>
                      <div className={`mt-2 text-sm ${
                        depositImpact.isOverLimit ? 'text-red-700' :
                        depositImpact.isNearLimit ? 'text-orange-700' :
                        'text-info-700'
                      }`}>
                        <p>• หลังบันทึกการแก้ไข: วงเงินรวมจะเป็น <strong>{depositImpact.newTotalDeposit.toLocaleString()}</strong> บาท</p>
                        <p>• การใช้งาน: <strong>{depositImpact.usageAfter.toFixed(1)}%</strong> ของวงเงินทั้งหมด</p>
                        <p>• วงเงินคงเหลือ: <strong>{depositImpact.remainingAfter.toLocaleString()}</strong> บาท</p>
                        {depositImpact.isOverLimit && (
                          <p className="mt-2 font-medium flex items-center"><RiAlertLine className="w-4 h-4 mr-1" /> เกินวงเงินมัดจำที่กำหนด — ยังบันทึกได้ (ผู้สร้างตัดสินใจเอง / ติ๊กฟรีมัดจำรายเครื่องได้)</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* วงเงินมัดจำ = advisory: ไม่มี hard error block อีกต่อไป (เกินวงเงิน → เตือนใน impact box ด้านบน) */}
            </div>
          </div>

          {/* ปุ่มบันทึก */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate('/rentals/list');
              }}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-[38px] bg-white py-2 px-4 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              ยกเลิก
            </button>
            {/* วงเงินมัดจำ = advisory (PO): เกินวงเงิน "ไม่บล็อก" — ปุ่มบันทึกใช้ได้เสมอ (เตือนสีแดงเฉยๆ) */}
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-[36px] py-2 px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <RiSaveLine className="h-4 w-4 mr-2" />
                  บันทึก
                </>
              )}
            </button>
          </div>
        </form>

        {/* Rental Notes Modal */}
        <RentalNotesModal
          isOpen={showNotesModal}
          onClose={() => setShowNotesModal(false)}
          rentalId={recordId ?? 0}
          rentalCode={formData.rental_number || ''}
        />
      </div>
    </Layout>
  );
};

export default RentalEditPageV2;